// Org risk dial card. Headline of the summary page: 0-100 gauge with
// band colour, delta from 30 days ago and a methodology link.
//
// Calculations: reads ORG_SCORE, ORG_BAND, ORG_DELTA from riskScore.js.
// No client-side computation; values arrive precomputed.
//
// Postgres mapping: `org_risk_snapshot` (org_id, computed_date, score,
// band, delta_30d) refreshed nightly.
// Integration hook: `useOrgRiskSummaryQuery()` cache 1h.
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import { motion } from 'motion/react';
import { ORG_SCORE, ORG_BAND, bandFor } from '../../riskScore';

// Users do not check the dashboard daily, so the indicator should anchor
// against the last time *this* viewer looked at it. Stored per-browser; the
// real implementation would persist this per-user via the portal.
const LAST_VISIT_KEY = 'rbx_lastvisit_orgscore';
const LAST_VISIT_AT_KEY = 'rbx_lastvisit_orgscore_at';

// 3/4 dial: 270deg arc, gap at bottom centre
// Start: 135deg SVG (bottom-left), sweep CW 270deg to 45deg (bottom-right)
const R = 48, CX = 70, CY = 70;
const toRad = (deg) => (deg * Math.PI) / 180;
const START_DEG = 135, SWEEP_DEG = 270;
const sx = CX + R * Math.cos(toRad(START_DEG));
const sy = CY + R * Math.sin(toRad(START_DEG));
const ex = CX + R * Math.cos(toRad(START_DEG + SWEEP_DEG));
const ey = CY + R * Math.sin(toRad(START_DEG + SWEEP_DEG));
const trackD = `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${R} ${R} 0 1 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`;
const trackLen = (SWEEP_DEG / 360) * 2 * Math.PI * R;

export default function RiskDialCard({ go, orgScore }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // null when no score exists - the dial then shows a no-data note instead
    // of asserting a green "Low / 0".
    const score = (typeof orgScore === 'number' ? orgScore : ORG_SCORE) ?? null;
    const band = score != null ? (bandFor(score) || ORG_BAND) : null;
    const color = band?.color || theme.palette.primary.main;

    // Pull the score recorded at the user's previous visit; store the
    // current score for next time. Initial visit shows a neutral "first
    // visit" indicator so the affordance is still self-explanatory.
    const [lastVisitScore, setLastVisitScore] = useState(null);
    const [lastVisitAt, setLastVisitAt] = useState(null);
    useEffect(() => {
        try {
            const stored = localStorage.getItem(LAST_VISIT_KEY);
            const storedAt = localStorage.getItem(LAST_VISIT_AT_KEY);
            if (stored !== null) {
                setLastVisitScore(parseInt(stored, 10));
                setLastVisitAt(storedAt);
            }
            if (score != null) {
                localStorage.setItem(LAST_VISIT_KEY, String(score));
                localStorage.setItem(LAST_VISIT_AT_KEY, new Date().toISOString());
            }
        } catch { /* localStorage disabled */ }
    }, [score]);

    const delta = lastVisitScore == null ? null : score - lastVisitScore;
    const deltaColor = delta == null ? '#A0A8B8' : delta > 0 ? '#FF5252' : delta < 0 ? '#66BB6A' : '#A0A8B8';
    const deltaArrow = delta == null ? '–' : delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
    const lastVisitLabel = (() => {
        if (!lastVisitAt) return 'first visit';
        const d = new Date(lastVisitAt);
        const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'today';
        if (diffDays === 1) return 'yesterday';
        if (diffDays < 7)   return `${diffDays} days ago`;
        if (diffDays < 30)  return `${Math.floor(diffDays / 7)}w ago`;
        return `${Math.floor(diffDays / 30)}mo ago`;
    })();

    if (score == null) {
        return (
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%', width: '100%' }} border={false} boxShadow={true}>
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>No risk score yet</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        The organisation score appears once users have simulation activity in the last 6 months.
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard
            content={false}
            sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', ...(isDark && { boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 4px 24px rgba(0,0,0,0.4)` }) }}
            border={false}
            boxShadow={true}
        >
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', gap: 1 }}>
                {/* Header */}
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Risk score
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Org-wide - last 6 months - lower is better
                    </Typography>
                </Box>

                {/* Middle, dial + since-last-visit. Wrapped in a flex-grow
                    container that centres both vertically and horizontally, so
                    when the card stretches to match siblings the extra height
                    becomes balanced breathing room around the dial instead of
                    a dead gap above the benchmark strip. */}
                <Box sx={{
                    flex: 1, minHeight: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 0.75,
                    py: 1,
                }}>
                    <Box sx={{ width: '100%', maxWidth: 220 }}>
                        <svg width="100%" viewBox="0 0 140 128" style={{ display: 'block', overflow: 'visible' }}>
                            <path d={trackD} fill="none" stroke={isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'} strokeWidth="10" strokeLinecap="round" />
                            <motion.path
                                d={trackD}
                                fill="none"
                                stroke={color}
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={trackLen}
                                initial={{ strokeDashoffset: trackLen }}
                                animate={{ strokeDashoffset: trackLen * (1 - score / 100) }}
                                transition={{ duration: 1.4, ease: [0.34, 1.0, 0.64, 1], delay: 0.2 }}
                            />
                            <text x={CX} y={CY - 4} textAnchor="middle" dominantBaseline="middle" fontSize="28" fontWeight="800" fill={theme.palette.text.primary} fontFamily="Inter,sans-serif" letterSpacing="-1">{score}</text>
                            <text x={CX} y={CY + 18} textAnchor="middle" dominantBaseline="middle" fontSize="9" fontWeight="700" fill={color} fontFamily="Inter,sans-serif" letterSpacing="0.5">
                                {band?.name?.toUpperCase()}
                            </text>
                        </svg>
                    </Box>

                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: deltaColor, textAlign: 'center', maxWidth: 220 }}>
                        {delta == null
                            ? 'No previous visit recorded'
                            : delta === 0
                                ? `No change since your last visit (${lastVisitLabel})`
                                : `${deltaArrow} ${Math.abs(delta)} pts since your last visit (${lastVisitLabel})`
                        }
                    </Typography>
                </Box>

                {/* Explicit CTA into the methodology page. The header help
                    icon stays for keyboard / muscle-memory users, but a
                    text button at the bottom is the discoverable entry
                    point most readers will actually find. */}
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => go && go('risk-methodology', 'Risk methodology')}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            borderRadius: '6px',
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 1.5, py: '4px',
                            borderColor: 'secondary.main',
                            color: 'secondary.main',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                borderColor: 'secondary.main',
                                backgroundColor: 'secondary.main',
                                color: 'common.white',
                            },
                            '& .MuiButton-endIcon': { transition: 'transform 200ms ease' },
                            '&:hover .MuiButton-endIcon': { transform: 'translateX(2px)' },
                        }}
                    >
                        What does this mean?
                    </Button>
                </Box>

            </Box>
        </MainCard>
    );
}
